import { OrderPageData, ResourceCalculationMode, SchedulerConfigurationData } from '../../api';
import { maskToIndexes, popcount } from '../../utils/maskUtils';
import { highestUsedIndex } from './maskRanges';

/**
 * Everything the editor can tell the admin before saving. The backend validates only mask shape,
 * page uniqueness and static-without-a-value — it never looks at orders or at whether a config
 * makes sense — so anything caught here is caught nowhere else.
 */
export enum ConfigGuardLevel {
  ERROR = 'error',
  WARNING = 'warning',
}

export type ConfigGuard = {
  level: ConfigGuardLevel;
  /** Suffix of `sharedResources.schedulerConfigs.guard_*`. */
  key: string;
  params?: Record<string, string | number>;
};

const pageName = (orderPages: OrderPageData[], orderPageId: string, language: string) => {
  const page = orderPages.find((candidate) => candidate.id === orderPageId);
  const label = page?.label as Record<string, string | undefined> | undefined;
  return label?.[language] || label?.he || orderPageId;
};

export const configGuards = (
  config: SchedulerConfigurationData,
  orderPages: OrderPageData[],
  language: string,
): ConfigGuard[] => {
  const guards: ConfigGuard[] = [];
  const areas = config.areas ?? [];
  const allowedMasks = config.allowed_masks ?? [];

  const minimumResourceCount =
    highestUsedIndex([...areas.map((area) => area.mask), ...allowedMasks.map((mask) => mask.mask)]) + 1;
  if (config.resource_count < minimumResourceCount) {
    guards.push({
      level: ConfigGuardLevel.ERROR,
      key: 'resourceCountTooLow',
      params: { min: minimumResourceCount },
    });
  }

  if (allowedMasks.length === 0) {
    // Nothing draws from this pool, so it has no effect on availability at all.
    guards.push({ level: ConfigGuardLevel.WARNING, key: 'noPages' });
  }

  allowedMasks.forEach((allowedMask) => {
    const name = pageName(orderPages, allowedMask.order_page_id, language);

    if (popcount(allowedMask.mask) === 0) {
      // A page with no resources cannot be booked at all once the scheduler owns it.
      guards.push({ level: ConfigGuardLevel.ERROR, key: 'pageHasNoResources', params: { page: name } });
    }

    if (allowedMask.resource_calculation_mode === ResourceCalculationMode.STATIC && !allowedMask.static_resources) {
      guards.push({ level: ConfigGuardLevel.ERROR, key: 'staticWithoutValue', params: { page: name } });
    }

    const pageIndexes = new Set(maskToIndexes(allowedMask.mask));
    (allowedMask.resource_options ?? []).forEach((resourceOption) => {
      const optionLabel = resourceOption.label as Record<string, string | undefined> | undefined;
      const optionName = optionLabel?.[language] || optionLabel?.he || optionLabel?.en || '';

      if (popcount(resourceOption.mask) === 0) {
        // The engine fails closed on an empty effective mask, so this option can never be booked.
        guards.push({
          level: ConfigGuardLevel.ERROR,
          key: 'optionHasNoResources',
          params: { page: name, option: optionName },
        });
      } else if (maskToIndexes(resourceOption.mask).some((index) => !pageIndexes.has(index))) {
        // The backend rejects an option mask that is not a subset of the page mask.
        guards.push({
          level: ConfigGuardLevel.ERROR,
          key: 'optionOutsidePageMask',
          params: { page: name, option: optionName },
        });
      }
    });

    const unnamedOptions = (allowedMask.resource_options ?? []).filter((resourceOption) => {
      const optionLabel = resourceOption.label as Record<string, string | undefined> | undefined;
      return !optionLabel || Object.values(optionLabel).every((value) => !value?.trim());
    }).length;
    if (unnamedOptions > 0) {
      guards.push({
        level: ConfigGuardLevel.WARNING,
        key: 'optionsWithoutName',
        params: { count: unnamedOptions, page: name },
      });
    }

    if (areas.length > 0) {
      const inAreas = new Set(areas.flatMap((area) => maskToIndexes(area.mask)));
      const outside = maskToIndexes(allowedMask.mask).filter((index) => !inAreas.has(index)).length;
      if (outside > 0) {
        // Editing the page's areas rebuilds its mask from the selection, which would drop these.
        guards.push({
          level: ConfigGuardLevel.WARNING,
          key: 'pageOutsideAreas',
          params: { page: name, count: outside },
        });
      }
    }
  });

  if (areas.length > 0) {
    const covered = new Set(areas.flatMap((area) => maskToIndexes(area.mask)));
    const uncovered = Array.from({ length: config.resource_count }, (unused, index) => index).filter(
      (index) => !covered.has(index),
    ).length;
    if (uncovered > 0) {
      // Orders holding these resources show in no pillar of the areas view.
      guards.push({ level: ConfigGuardLevel.WARNING, key: 'resourcesWithoutArea', params: { count: uncovered } });
    }
  }

  const unnamed = areas.filter((area) => {
    const label = area.label as Record<string, string | undefined> | undefined;
    return !label || Object.values(label).every((value) => !value?.trim());
  }).length;
  if (unnamed > 0) {
    guards.push({ level: ConfigGuardLevel.WARNING, key: 'areasWithoutName', params: { count: unnamed } });
  }

  return guards;
};

export const hasErrors = (guards: ConfigGuard[]): boolean =>
  guards.some((guard) => guard.level === ConfigGuardLevel.ERROR);
